/**
 * Transport time reconciler.
 *
 * Enforces the invariant: arrivalTime ≈ departureTime + duration + tzDiff.
 *
 * The LLM and various heuristic estimators all produce transport entries
 * (flights, drives, ferries, trains) with a `departureTime`, `arrivalTime`,
 * and `duration` triplet. There's no shared post-step that checks the three
 * agree. Two production bugs surfaced from this:
 *
 *   - "Day 6 flight: 9:00 AM departure, 4h30m, Arrives 08:00"
 *     (LLM hallucinated arrival, validateFlightTransport trusted it)
 *
 *   - "Day 9 drive: Depart 1:00 PM · -4 hr"
 *     (LLM hallucinated arrival earlier than departure; render computes
 *     duration = arrival - departure → negative)
 *
 * Both are the same class of bug. Rather than patch each transport-mode
 * branch separately, this module is a single deterministic reconciler that
 * any transport-producing path should call before pushing the entry to the
 * day timeline.
 *
 * @module transportReconciler
 */

/**
 * Reconcile a transport entry's arrival time against its departure time
 * and duration. If the LLM (or any upstream source) produced inconsistent
 * values, recompute arrival from departure + duration + timezone diff.
 *
 * The function is non-destructive — it returns a new entry only if a
 * correction was made. The original is returned untouched if the times
 * already agree (within tolerance) or if any input is missing.
 *
 * @param {object} entry - transport entry. Required: { departureTime, duration }.
 *                          Optional: { arrivalTime, arrivalDayOffset }.
 * @param {object} opts - { timezoneDiff?: number, tolerance?: number }.
 *                         timezoneDiff: hours, positive = destination ahead.
 *                         tolerance: minutes of allowed drift before override (default 30).
 * @returns {object} entry with corrected arrivalTime/arrivalDayOffset if needed.
 */
export function reconcileTransportTimes(entry, opts = {}) {
  if (!entry || typeof entry !== 'object') return entry;

  const { departureTime, arrivalTime, duration } = entry;
  if (!departureTime || !duration) return entry;

  const depMin = parseTimeStringToMinutes(departureTime);
  const durMin = parseDurationToMinutesLocal(duration);
  if (depMin == null || durMin == null) return entry;

  const tzDiff = typeof opts.timezoneDiff === 'number' ? opts.timezoneDiff : 0;
  // B5 fix (May 2026): tolerance lowered from 30→15 minutes. The previous
  // 30-min tolerance was masking real arrival-time drifts that compounded
  // across multi-leg trips. 15min is still generous (covers normal LLM
  // rounding) but catches the cases that previously slipped through.
  const tolerance = typeof opts.tolerance === 'number' ? opts.tolerance : 15;

  // Expected arrival in the destination's local time.
  // arrival_local = departure_local + duration + (toTz - fromTz)
  // The day offset is whatever number of full days that crosses.
  const expectedRaw = depMin + durMin + tzDiff * 60;
  const expectedDayOffset = Math.floor(expectedRaw / 1440);
  const expectedArrMin = ((expectedRaw % 1440) + 1440) % 1440;
  const expectedArrTime = formatMinutesAsTime(expectedArrMin);

  // No arrivalTime — fill it in.
  if (!arrivalTime) {
    return {
      ...entry,
      arrivalTime: expectedArrTime,
      arrivalDayOffset: expectedDayOffset,
      _arrivalEstimated: true,
    };
  }

  const arrMin = parseTimeStringToMinutes(arrivalTime);
  if (arrMin == null) return entry;

  // Compare actual to expected. We use total minutes (including day offset)
  // so a "+1d" arrival that happens to land at the same wall-clock hour as
  // departure isn't accidentally treated as drift-free.
  const actualDayOffset = typeof entry.arrivalDayOffset === 'number' ? entry.arrivalDayOffset : null;
  const actualRaw = actualDayOffset != null
    ? arrMin + actualDayOffset * 1440
    : (() => {
        // No explicit day offset — pick the offset that minimizes drift from
        // the expected raw. This keeps us forgiving of entries that just
        // omitted arrivalDayOffset rather than getting it wrong.
        const candidates = [
          arrMin + (expectedDayOffset - 1) * 1440,
          arrMin + expectedDayOffset * 1440,
          arrMin + (expectedDayOffset + 1) * 1440,
        ];
        return candidates.reduce((best, c) =>
          Math.abs(c - expectedRaw) < Math.abs(best - expectedRaw) ? c : best
        );
      })();

  const drift = Math.abs(actualRaw - expectedRaw);
  if (drift <= tolerance) return entry; // close enough

  // Arrival is inconsistent with departure + duration. Trust duration —
  // it's the most reliably-estimated field across our pipeline (route-rule
  // heuristics for flights, Google Directions for drives, fixed schedule
  // for ferries) — and recompute arrival.
  return {
    ...entry,
    arrivalTime: expectedArrTime,
    arrivalDayOffset: expectedDayOffset,
    _timeReconciled: true,
    _reconcileReason: `arrival drift ${drift}min > tolerance ${tolerance}min`,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Parse a "HH:MM" or "H:MM" or "HH:MM AM/PM" string to minutes-since-midnight.
 * Returns null if unparseable.
 */
function parseTimeStringToMinutes(s) {
  if (typeof s !== 'string') return null;
  const trimmed = s.trim();

  // 24-hour form (most common in this codebase)
  const m24 = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!m24) return null;

  let h = parseInt(m24[1], 10);
  const min = parseInt(m24[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min) || min < 0 || min >= 60) return null;

  // 12-hour AM/PM suffix — convert to 24-hour
  const ampm = trimmed.match(/(am|pm)\s*$/i);
  if (ampm) {
    const isPm = ampm[1].toLowerCase() === 'pm';
    if (h === 12) h = isPm ? 12 : 0;
    else if (isPm) h += 12;
  }

  if (h < 0 || h >= 24) return null;
  return h * 60 + min;
}

/**
 * Local copy of duration parser to keep this module standalone.
 * Accepts "Xh Ym", "X.Yh", "Xh", "Ym", "~Xh", "X-Yh" (uses midpoint).
 * Returns null if unparseable.
 */
function parseDurationToMinutesLocal(d) {
  if (typeof d !== 'string') return null;
  const s = d.trim().toLowerCase().replace(/^~/, '').replace(/\s+/g, '');

  // Range form (e.g. "5-7h") — use midpoint
  const range = s.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)h/);
  if (range) {
    const lo = parseFloat(range[1]);
    const hi = parseFloat(range[2]);
    return Math.round(((lo + hi) / 2) * 60);
  }

  // "Xh Ym" or "Xh"
  const hm = s.match(/^(\d+)h(\d+)m?$/);
  if (hm) return parseInt(hm[1], 10) * 60 + parseInt(hm[2], 10);

  const hOnly = s.match(/^(\d+(?:\.\d+)?)h$/);
  if (hOnly) return Math.round(parseFloat(hOnly[1]) * 60);

  const mOnly = s.match(/^(\d+)m$/);
  if (mOnly) return parseInt(mOnly[1], 10);

  // Fallback: just numbers (assume hours if < 24, minutes otherwise)
  const n = parseFloat(s);
  if (!Number.isNaN(n)) {
    return n < 24 ? Math.round(n * 60) : Math.round(n);
  }

  return null;
}

function formatMinutesAsTime(mins) {
  const m = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60) % 24;
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

// ─── Country-level transport constraints ─────────────────────────────────
// Roads either don't exist or aren't usable for inter-stop moves in these
// countries — most are atoll/island states where each stop is a different
// island and the only realistic transport is ferry, speedboat, or seaplane.
//
// This is a curated MVP list (top resort-trip destinations affected by the
// "drive in the Maldives" bug); a complete dataset would live in a country-
// metadata table. For now, it covers the cases users actually book.
export const ROAD_IMPOSSIBLE_COUNTRIES = new Set([
  'Maldives',
  'Seychelles',
  'Marshall Islands',
  'Tuvalu',
  'Kiribati',
  'Palau',
  'Federated States of Micronesia',
  'Micronesia',
  'Nauru',
]);

/**
 * True when neither end of the leg can plausibly be reached by road from
 * the other — used to override a `mode: 'car'` fallback before it reaches
 * the user.
 */
export function isInterIslandRoadImpossible(fromCountry, toCountry) {
  if (!fromCountry && !toCountry) return false;
  // Either end being a no-roads country flags the leg, since we don't know
  // which direction the user is moving and a single-island intra-country
  // move would be handled at a finer level (same baseCity).
  return ROAD_IMPOSSIBLE_COUNTRIES.has(fromCountry) ||
         ROAD_IMPOSSIBLE_COUNTRIES.has(toCountry);
}

// ─── Dynamic resort-level non-road detection ─────────────────────────────
//
// The country-level guard above only catches all-island countries (Maldives,
// Seychelles). It MISSES intra-country sea crossings:
//   • Sydney → Great Barrier Reef resort (same country, but boat/seaplane)
//   • Mainland China → Hainan Island
//   • Mainland Greece → Santorini
//   • Mainland Italy → Capri / Ischia
//   • Vancouver → Hayman Island (BC inland resort)
//
// `fetchResortArrivalInfo` (services/generationOrchestrator.js) already
// pulls real transfer info from Brave Search and parses it into a
// `transferModes` array — `['seaplane', 'speedboat']` for Hayman Island,
// `['ferry']` for Hydra, etc. If the resort's arrival info exists AND the
// only modes listed are sea/air, road is dynamically known to be wrong.
//
// This is the data-driven counterpart to ROAD_IMPOSSIBLE_COUNTRIES — covers
// the long tail of intra-country resort destinations without needing a
// curated list per country.

const ROAD_TRANSFER_MODES = new Set([
  'private transfer', 'shuttle', 'private car', 'taxi', 'rental car',
  'uber', 'minivan', 'town car', 'limousine', 'sedan', 'car',
]);

/**
 * Returns true when the resort's arrival data lists ONLY non-road transfer
 * modes (seaplane, speedboat, ferry, helicopter, bush plane). Returns false
 * when the data includes any road option, or when no transfer data is
 * available (insufficient signal to override).
 *
 * Use this AFTER the country-level guard — if either fires, road is
 * impossible.
 */
export function resortRequiresNonRoadTransfer(resortArrivalData) {
  const modes = resortArrivalData?.transferModes;
  if (!Array.isArray(modes) || modes.length === 0) return false;
  // If ANY mode is a road option, road is viable — leave the user's
  // override alone. Only flag non-road when EVERY mode is sea/air.
  const hasAnyRoadMode = modes.some(m =>
    typeof m === 'string' && ROAD_TRANSFER_MODES.has(m.toLowerCase())
  );
  return !hasAnyRoadMode;
}

// ─── Safari/remote-resort flight requirement ────────────────────────────
//
// Safari camps and other remote lodges sit on roads, but the realistic
// arrival is always by air (bush plane to a nearby airstrip, then a
// short 4x4 transfer). `transferModes` for a Sabi Sand or Serengeti lodge
// typically looks like ['bush plane', 'private transfer'] — and our
// `resortRequiresNonRoadTransfer()` returns FALSE for that because
// 'private transfer' is in ROAD_TRANSFER_MODES.
//
// This helper is the stronger sibling: if the resort's transfer modes
// include any "you must fly to get here" indicator (bush plane, light
// aircraft, charter flight, helicopter, seaplane), the leg's primary
// mode should be 'flight' regardless of the road component.
//
// Self-driving to most safari camps is impossible (gated reserves, no
// rental return, often illegal). The bush plane is the leg, the private
// transfer is the last 30 minutes — display-side we want to show flight.
const REQUIRES_FLIGHT_MODES = new Set([
  'bush plane', 'light aircraft', 'charter flight', 'charter plane',
  'helicopter', 'seaplane', 'domestic flight', 'private jet',
]);

/**
 * Returns true when the resort's arrival data includes any indicator that
 * the leg's primary mode is air — even if a road final-mile component is
 * also listed. Use this BEFORE the road check so it wins over a permissive
 * 'private transfer' entry.
 */
export function resortRequiresFlight(resortArrivalData) {
  const modes = resortArrivalData?.transferModes;
  if (!Array.isArray(modes) || modes.length === 0) return false;
  return modes.some(m =>
    typeof m === 'string' && REQUIRES_FLIGHT_MODES.has(m.toLowerCase())
  );
}
